/* eslint-disable max-lines */
import { getTaskClassName } from './timelineTaskHelpers';
import { createUserGroup } from './timelineGroupHelpers';
import { formatInTimeZone } from 'date-fns-tz';
import type { TimeFormatPreference } from '@/utils/timeFormat';
import {
    UserData, RosterShift, ShiftTaskItem, TeamData, TimelineItem, TimelineGroup
} from './types';

import {
    getContrastColor,
    getDatesWithFormatting,
    escapeAndFormatComment,
    hasCommentText,
    getCommentPrefix,
    applyDateRangeBuffer
} from './timelineHelpers';
import { isShiftHiddenLeave } from '@/components/TeamAndSchedule/utils/rosterTransformUtils';

interface LegendItem {
    name: string;
    color: string;
}

interface ColumnDisplay {
    userId?: boolean;
    email?: boolean;
}

/**
 * Sorts timeline data by team name (if grouped) and then by user name
 * @param data - The raw timeline data
 * @param groupBy - The grouping strategy ('TEAMS' or 'NONE')
 * @returns Sorted data array
 */
export const sortTimelineData = (data: UserData[], groupBy: string): UserData[] => {
    return [...data].sort((a: UserData, b: UserData) => {
        if (groupBy === 'TEAMS') {
            const teamA = (a.user?.teamName || 'Blanks').toLowerCase();
            const teamB = (b.user?.teamName || 'Blanks').toLowerCase();
            const teamCompare = teamA.localeCompare(teamB);
            if (teamCompare !== 0) return teamCompare;
        }
        const nameA = (a.user.name || a.user.email).toLowerCase();
        const nameB = (b.user.name || b.user.email).toLowerCase();
        return nameA.localeCompare(nameB);
    });
};

/**
 * Creates groups array for the timeline based on grouping strategy
 * @param sortedData - Pre-sorted timeline data
 * @param groupBy - The grouping strategy ('TEAMS' or 'NONE')
 * @param columnDisplay - Configuration for which columns to display
 * @returns Array of group objects for Vis.js Timeline
 */
export const createGroupsArray = (
    sortedData: UserData[],
    groupBy: string,
    columnDisplay: ColumnDisplay
): TimelineGroup[] => {
    if (groupBy === 'TEAMS') {
        const teamMap = new Map();

        sortedData.forEach((userData: UserData) => {
            const teamName = userData.user?.teamName || 'Blanks';
            const teamId = userData.user?.teamId || 0;

            if (!teamMap.has(teamId)) {
                teamMap.set(teamId, {
                    teamName,
                    teamId,
                    users: []
                });
            }
            teamMap.get(teamId).users.push(userData);
        });

        const groupsArray: TimelineGroup[] = [];
        teamMap.forEach((teamData: TeamData, teamId: number) => {
            const nested = teamData.users.map((u: UserData) => `user-${u.user.id}`);

            groupsArray.push({
                // Prefix team ids to avoid collision with user ids
                id: `team-${teamId}`,
                content: `${teamData.teamName} (${teamData.users.length})`,
                nestedGroups: nested,
                showNested: true,
                treeLevel: 0
            });

            teamData.users.forEach((userData: UserData) => {
                const userGroup = createUserGroup(userData, columnDisplay, groupBy);
                groupsArray.push(userGroup as TimelineGroup);
            });
        });

        return groupsArray;
    } else {
        // When not grouped, return a single group row per user
        return sortedData.map((userData: UserData) => createUserGroup(userData, columnDisplay, groupBy) as TimelineGroup);
    }
};

/**
 * Parses timeline items from user shift data
 * @param sortedData - Pre-sorted timeline data
 * @param timezone - Optional timezone for converting UTC times to user timezone
 * @param timeFormatPreference - User's time format preference (12h or 24h)
 * @param hasViewLeaveTypePermission - Whether user has permission to view leave type names
 * @returns Object containing items array and legend items
 */
export function parseTimelineItems(
    data: UserData[],
    timezone?: string,
    timeFormatPreference?: TimeFormatPreference | null,
    hasViewLeaveTypePermission: boolean = false
): {
    items: TimelineItem[];
    groups: TimelineGroup[];
    legendItems: Array<{ name: string; color: string }>;
} {
    const itemsArray: TimelineItem[] = [];
    const uniqueTasks = new Map<string, LegendItem>();
    const itemIdCounterRef = { value: 1 };

    data.forEach((userData: UserData) => {
        const baseId = `user-${userData.user.id}`;

        // For each shift, first create task items then push a single shift-range item into the shift lane
        userData.shifts.forEach((shift: RosterShift) => {
            // Skip hidden leave shifts
            if (isShiftHiddenLeave(shift)) {
                return;
            }
            
            // Create a shift block item
            let shiftStart: Date;
            let shiftEnd: Date;
            let shiftStartFormatted: string;
            let shiftEndFormatted: string;

            if (timezone) {
                const startInTz = formatInTimeZone(new Date(shift.startTime), timezone, "yyyy-MM-dd'T'HH:mm:ss");
                const endInTz = formatInTimeZone(new Date(shift.endTime), timezone, "yyyy-MM-dd'T'HH:mm:ss");
                shiftStart = new Date(startInTz);
                shiftEnd = new Date(endInTz);
                shiftStartFormatted = formatInTimeZone(new Date(shift.startTime), timezone, 'PPpp');
                shiftEndFormatted = formatInTimeZone(new Date(shift.endTime), timezone, 'PPpp');
            } else {
                shiftStart = new Date(shift.startTime);
                shiftEnd = new Date(shift.endTime);
                shiftStartFormatted = shiftStart.toLocaleString();
                shiftEndFormatted = shiftEnd.toLocaleString();
            }

            // Filter out invalid tasks first and then process only valid tasks
            const validTasks = (shift.shiftTasks || []).filter((t: ShiftTaskItem) => isValidTask(t));

            validTasks.forEach((task: ShiftTaskItem) => {
                processTask({
                    task,
                    userId: userData.user.id,
                    collections: {
                        uniqueTasks, itemsArray, itemIdCounterRef
                    },
                    helpers: { getContrastColor, getTaskClassName },
                    timezone,
                    userBaseId: baseId,
                    timeFormatPreference,
                    hasViewLeaveTypePermission
                } as ProcessTaskOptions);
            });

            // Always render the shift block so that shifts with no tasks are still visible in the timeline
            // (but skip if shift range is invalid to avoid pushing items with Invalid Date start/end)
            const isValidShiftRange =
                !Number.isNaN(shiftStart.getTime()) &&
                !Number.isNaN(shiftEnd.getTime()) &&
                shiftStart <= shiftEnd;

            if (isValidShiftRange) {
                collectionsPushShiftItem({
                    itemsArray,
                    itemIdCounterRef,
                    userBaseId: baseId,
                    shiftStart,
                    shiftEnd,
                    shift,
                    shiftStartFormatted,
                    shiftEndFormatted,
                    timezone,
                    timeFormatPreference
                });
            }
        });
    });

    const legendItems = Array.from(uniqueTasks.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
    );

    const groups: TimelineGroup[] = [];

    return {
        items: itemsArray, groups, legendItems
    };
}

/**
 * Calculates min and max dates from timeline items
 * @param items - Array of timeline items
 * @param fallbackStart - Optional fallback start date when no items exist
 * @param fallbackEnd - Optional fallback end date when no items exist
 * @returns Object with minDate and maxDate
 */
export const calculateDateRange = (
    items: TimelineItem[],
    fallbackStart?: Date,
    fallbackEnd?: Date
): {
    minDate: Date | null;
    maxDate: Date | null;
} => {
    // If no items, use fallback dates if provided
    if (!items || items.length === 0) {
        if (fallbackStart && fallbackEnd) {
            return { minDate: fallbackStart, maxDate: fallbackEnd };
        }
        return { minDate: null, maxDate: null };
    }

    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    items.forEach((item) => {
        if (!minDate || item.start < minDate) minDate = item.start;
        if (!maxDate || item.end > maxDate) maxDate = item.end;
    });

    if (!minDate || !maxDate) {
        return { minDate, maxDate };
    }

    // Set minDate to start of the day
    const startOfDay = new Date(minDate);
    startOfDay.setHours(0, 0, 0, 0);

    // Set maxDate to end of the day
    const endOfDay = new Date(maxDate);
    endOfDay.setHours(23, 59, 59, 999);

    // Apply buffer if needed and return
    return applyDateRangeBuffer(startOfDay, endOfDay);
};

interface ProcessTaskHelpers {
    getContrastColor: (color: string) => string;
    getTaskClassName: (name: string) => string;
}

interface ProcessTaskCollections {
    uniqueTasks: Map<string, LegendItem>;
    itemsArray: TimelineItem[];
    itemIdCounterRef: { value: number };
}

function isValidTask(task: ShiftTaskItem): boolean {
    return !!(
        task.taskType &&
        task.taskType.name &&
        task.startTime &&
        task.endTime
    );
}

// Helper: build full tooltip/title for a task
function buildTaskTitle(
    task: ShiftTaskItem,
    _timezone?: string,
    timeFormatPreference?: TimeFormatPreference | null,
    hasViewLeaveTypePermission?: boolean
) {
    const taskType = task.taskType!;
    // Use helper to get Date objects (handles timezone when provided)
    const { start, end } = getDatesWithFormatting(task.startTime!, task.endTime!, _timezone);

    // Format a Date into time string based on user preference
    const is12HourFormat = timeFormatPreference?.timeFormat === '12h';
    const fmtTime = (d?: Date) => {
        if (!d) return '';
        if (is12HourFormat) {
            // 12-hour format with AM/PM (padded hours for alignment)
            let hours = d.getHours();
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12; // Convert to 12-hour format (0 becomes 12)
            const paddedHours = String(hours).padStart(2, '0'); // Pad single-digit hours
            return `${paddedHours}:${minutes} ${ampm}`;
        } else {
            // 24-hour format (HH:mm)
            const hh = String(d.getHours()).padStart(2, '0');
            const mm = String(d.getMinutes()).padStart(2, '0');
            return `${hh}:${mm}`;
        }
    };

    const startStr = fmtTime(start);
    const endStr = fmtTime(end);
    
    // Show task type name only if user has permission OR if it's not a leave task
    let taskName = taskType.name;
    if (taskType.isLeave && !hasViewLeaveTypePermission) {
        taskName = 'Leave';
    }
    
    const firstLine = `${taskName}: ${startStr}${startStr && endStr ? ` - ${endStr}` : ''}`;

    const hasComment = hasCommentText(task.comment);
    const commentHtml = hasComment ? escapeAndFormatComment(task.comment) : '';

    const parts: string[] = [];
    parts.push(firstLine);
    if (commentHtml) parts.push(commentHtml);

    // Join with <br/> and return without div wrapper to match Table view
    return parts.join('<br/>');
}

// Helper: build full tooltip/title for a shift
function buildShiftTitle(
    shift: RosterShift,
    _opts?: {
        shiftStartFormatted?: string;
        shiftEndFormatted?: string;
        shiftStart?: Date;
        shiftEnd?: Date;
        timeFormatPreference?: TimeFormatPreference | null;
    }
) {
    // Format a Date/string into time string based on user preference
    const is12HourFormat = _opts?.timeFormatPreference?.timeFormat === '12h';
    const fmtTime = (d?: Date | string) => {
        if (!d) return '';
        const date = d instanceof Date ? d : new Date(d);
        if (is12HourFormat) {
            // 12-hour format with AM/PM (padded hours for alignment)
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12; // Convert to 12-hour format (0 becomes 12)
            const paddedHours = String(hours).padStart(2, '0'); // Pad single-digit hours
            return `${paddedHours}:${minutes} ${ampm}`;
        } else {
            // 24-hour format (HH:mm)
            const hh = String(date.getHours()).padStart(2, '0');
            const mm = String(date.getMinutes()).padStart(2, '0');
            return `${hh}:${mm}`;
        }
    };

    const startStr = fmtTime(_opts?.shiftStart ?? shift.startTime);
    const endStr = fmtTime(_opts?.shiftEnd ?? shift.endTime);

    const firstLine = `Shift: ${startStr}${startStr && endStr ? ` - ${endStr}` : ''}`;

    const hasComment = hasCommentText(shift.comment);
    const commentHtml = hasComment ? escapeAndFormatComment(shift.comment) : '';

    const parts: string[] = [];
    parts.push(firstLine);
    if (commentHtml) parts.push(commentHtml);

    // Join with <br/> and return without div wrapper to match Table view
    return parts.join('<br/>');
        
}

interface ProcessTaskOptions {
    task: ShiftTaskItem;
    userId: number;
    collections: ProcessTaskCollections;
    helpers: ProcessTaskHelpers;
    timezone?: string;
    userBaseId?: string;
    timeFormatPreference?: TimeFormatPreference | null;
    hasViewLeaveTypePermission?: boolean;
}

function processTask(options: ProcessTaskOptions) {
    const { task, userId, collections, helpers, timezone, userBaseId, timeFormatPreference, hasViewLeaveTypePermission } = options;
    
    if (!isValidTask(task)) return;
    
    const taskType = task.taskType!;
    let taskName = taskType.name;
    let color = `#${taskType.colour}`;
    
    // Apply leave permission check - hide specific leave type if user doesn't have permission
    if (taskType.isLeave && !hasViewLeaveTypePermission) {
        taskName = 'Leave';
        color = '#E8EDED'; // Gray color for leave when permission is denied
    }
    
    const textColor = helpers.getContrastColor(color);
    // update legend/unique tasks map
    addUniqueTaskIfMissing(collections.uniqueTasks, taskName, color);

    const { start, end } = getDatesWithFormatting(task.startTime!, task.endTime!, timezone);
    const fullTitle = buildTaskTitle(task, timezone, timeFormatPreference, hasViewLeaveTypePermission);
    // Ensure the info icon uses the same text color as the task content
    const commentPrefix = getCommentPrefix(task.comment, textColor);

    collections.itemsArray.push({
        id: collections.itemIdCounterRef.value++,
        group: userBaseId ? userBaseId : userId,
        content: `${commentPrefix}${taskName}`,
        start,
        end,
        type: 'range',
        style: `background-color: ${color}; border-color: ${color}; color: ${textColor};`,
        className: helpers.getTaskClassName(taskName),
        title: fullTitle
    });
}

// Helper to add unique task entry if not present
function addUniqueTaskIfMissing(map: Map<string, LegendItem>, name: string, color: string) {
    if (!map.has(name)) map.set(name, { name, color });
}

// Helper to insert a shift block item into the shift lane for a user
function collectionsPushShiftItem(opts: {
    itemsArray: TimelineItem[];
    itemIdCounterRef: { value: number };
    userBaseId: string;
    shiftStart: Date;
    shiftEnd: Date;
    shift: RosterShift;
    shiftStartFormatted?: string;
    shiftEndFormatted?: string;
    timezone?: string;
    timeFormatPreference?: TimeFormatPreference | null;
}) {
    const {
        itemsArray,
        itemIdCounterRef,
        userBaseId,
        shiftStart,
        shiftEnd,
        shift,
        shiftStartFormatted,
        shiftEndFormatted,
        timeFormatPreference
    } = opts;

    const shiftTitle = buildShiftTitle(shift, {
        shiftStartFormatted, shiftEndFormatted, shiftStart, shiftEnd, timeFormatPreference
    });
    // The shift block uses a fixed text color in its style - keep the info icon consistent
    const shiftCommentPrefix = getCommentPrefix(shift.comment, '#1a2021');

    itemsArray.push({
        id: itemIdCounterRef.value++,
        group: userBaseId,
        content: `${shiftCommentPrefix}${(shift.shiftType || '')}`,
        start: shiftStart,
        end: shiftEnd,
        type: 'range',
        style: 'background: #D5DDF6; border: 1px solid #97B0F8; color: #1a2021; height:12px !important',
        className: 'shift-block',
        title: shiftTitle
    });
}
